import {
  ProductSource,
  ProductSummary,
  ProductSummaryFieldsFragment,
  SaveListInput,
  ShoppingListsDetailQuery,
  SponsoredProductFieldsFragment,
} from '@fdc/__generated__/graphql';
import { LISTS_OP_TYPE } from '@fdc/constants/lists-constants';

export const createShoppingListPayload = (
  name: string,
  inputReferenceId?: string,
  inputTimestamp?: number
) => {
  const referenceId = inputReferenceId ?? crypto.randomUUID();
  const timestamp = inputTimestamp ?? Math.floor(Date.now() / 1000);

  return {
    referenceId,
    name,
    timestamp,
  };
};

export const createDuplicateShoppingListPayload = (
  existingList: ShoppingListsDetailQuery['list'],
  newListName: string
) => {
  const referenceId = crypto.randomUUID();
  const timestamp = Math.floor(Date.now() / 1000);

  const freeTexts = existingList?.freeTexts?.map((item) =>
    createShoppingListFreeTextPayload(item.text, timestamp)
  );

  const products = existingList?.products?.map((item) =>
    createShoppingListProductPayload({
      articleNumber: item.articleNumber,
      variantKey: item.variantKey,
      quantity: item.quantity,
      inputTimestamp: timestamp,
    })
  );

  return {
    referenceId,
    name: newListName,
    timestamp,
    freeTexts,
    products,
  };
};

export const createShoppingListFreeTextPayload = (
  text: string,
  inputTimestamp?: number
) => {
  const timestamp = inputTimestamp ?? Math.floor(Date.now() / 1000);

  return {
    opType: LISTS_OP_TYPE.CREATE,
    text,
    timestamp,
  };
};

export const createShoppingListFreeTextDeletePayload = (
  id: string,
  inputTimestamp?: number
) => {
  const timestamp = inputTimestamp ?? Math.floor(Date.now() / 1000);

  return {
    opType: LISTS_OP_TYPE.DELETE,
    id: Number(id),
    timestamp,
  };
};

export const createShoppingListProductPayload = ({
  articleNumber,
  variantKey,
  quantity,
  inputTimestamp,
}: {
  articleNumber: string;
  variantKey: string;
  quantity: number;
  inputTimestamp?: number;
}) => {
  const timestamp = inputTimestamp ?? Math.floor(Date.now() / 1000);
  return {
    opType: LISTS_OP_TYPE.CREATE,
    articleNumber,
    quantity,
    variantKey,
    timestamp,
  };
};

export const createShoppingListProductUpdatePayload = ({
  id,
  articleNumber,
  variantKey,
  quantity,
  inputTimestamp,
}: {
  id: string;
  articleNumber: string;
  variantKey: string;
  quantity: number;
  inputTimestamp?: number;
}) => {
  const timestamp = inputTimestamp ?? Math.floor(Date.now() / 1000);
  return {
    opType: LISTS_OP_TYPE.UPDATE,
    id: Number(id),
    articleNumber,
    variantKey,
    quantity,
    timestamp,
  };
};

export const createShoppingListProductDeletePayload = (
  id: string,
  inputTimestamp?: number
) => {
  const timestamp = inputTimestamp ?? Math.floor(Date.now() / 1000);
  return {
    opType: LISTS_OP_TYPE.DELETE,
    id: Number(id),
    timestamp,
  };
};

export const mapCartLineItemsToShoppingListProducts = (
  lineItems: Array<{
    sku: string;
    productVariantSku?: string;
    quantity: number;
  }>
) =>
  lineItems.map((lineItem) =>
    createShoppingListProductPayload({
      articleNumber: lineItem.sku,
      variantKey: lineItem.productVariantSku ?? '',
      quantity: lineItem.quantity,
    })
  );

export const createSaveProductsToListsPayloads = (
  selectedLists: Array<{ name: string; referenceId?: string }>,
  products: ReturnType<typeof createShoppingListProductPayload>[]
): SaveListInput[] =>
  selectedLists.map((list) => ({
    ...createShoppingListPayload(list.name, list.referenceId),
    products,
  }));

export const saveProductsToLists = async (
  selectedLists: Array<{ name: string; referenceId?: string }>,
  products: ReturnType<typeof createShoppingListProductPayload>[],
  handleSaveShoppingList: (payload: SaveListInput) => Promise<unknown>
) => {
  const payloads = createSaveProductsToListsPayloads(selectedLists, products);

  return Promise.all(
    payloads.map((payload) => handleSaveShoppingList(payload))
  );
};

export const groupProductsByCategory = (
  products: ProductSummaryFieldsFragment[]
): {
  category: string;
  products: ProductSummaryFieldsFragment[];
}[] => {
  const categorized = new Map<string, ProductSummaryFieldsFragment[]>();

  products.forEach((product) => {
    const category = product.categoryHierarchyNames?.lvl1?.[0] ?? 'Other';
    const existing = categorized.get(category) ?? [];
    existing.push(product);
    categorized.set(category, existing);
  });

  return Array.from(categorized, ([category, categoryProducts]) => ({
    category,
    products: categoryProducts,
  }));
};

export const getProductSummaryVariant = (
  product: ProductSummaryFieldsFragment | SponsoredProductFieldsFragment
) => {
  const variant =
    product.variants?.find((v) => v.unitOfMeasure === 'EACH') ??
    product.variants?.[0];
  const variantKey = variant?.variantKey ?? '';
  const variantMinQuantity = variant?.purchaseUnit?.minimumQty ?? 1;

  return { variantKey, variantMinQuantity };
};

export function mapProductSummaryFieldsFragmentToProductSummary(
  product: ProductSummaryFieldsFragment
): ProductSummary {
  return {
    __typename: 'ProductSummary',
    key: product.sku,
    sku: product.sku,
    productName: product.productName,
    slug: product.slug,
    imageUrl: product.imageUrl,
    storeKey: product.storeKey,
    productGroup: product.productGroup?.map((group) => ({
      __typename: 'ProductGroup',
      description: group.description,
      id: group.id,
      name: '',
    })),
    isAlcohol: product.isAlcohol,
    isTobacco: product.isTobacco,
    healthStarRating: product.healthStarRating,
    brand: product.brand,
    tags: product.tags,
    categoryHierarchyNames: product.categoryHierarchyNames,
    variants: product.variants.map((variant) => ({
      __typename: 'VariantSummary',
      variantKey: variant.variantKey,
      assets: variant.assets,
      name: variant.name,
      unitOfMeasure: variant.unitOfMeasure,
      stockOnHand: variant.stockOnHand,
      availabilityStatus: variant.availabilityStatus,
      purchaseUnit: variant.purchaseUnit,
      sortOrder: 0,
      variantPrice: variant.variantPrice && {
        ...variant.variantPrice,
        canShowOriginalPrice: false,
        canShowSavings: false,
        hasBonusPoints: false,
      },
    })),
    availabilityStatus: '',
    customAttributes: [],
    dynamicTags: [],
    metadata: { source: ProductSource.Algolia },
  };
}
